import React from "react";
import { Star, Phone, ArrowRight, ShieldCheck } from "lucide-react";
import SEO from "../components/SEO";
import { PHONE, PHONE_TEL, BRAND, GOOGLE_REVIEW_URL } from "../data/content";

export default function Review() {
  return (
    <>
      <SEO
        title={`Leave a Google Review | ${BRAND} Adelaide`}
        description="Loved the work? A quick 30-second Google review helps a small Adelaide IT business more than you might think. Tap to leave one."
        path="/review"
      />

      <section className="pt-20 pb-12 te-noise relative overflow-hidden">
        <div
          className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-10 blur-3xl pointer-events-none"
          style={{ background: "var(--te-primary)" }}
        />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
          {/* 5 stars */}
          <div className="flex justify-center gap-1 mb-6" data-testid="review-stars">
            {[0, 1, 2, 3, 4].map((i) => (
              <Star
                key={i}
                size={32}
                fill="#FF3B30"
                stroke="#FF3B30"
                style={{ animation: `te-pop 0.4s ease ${i * 0.08}s both` }}
              />
            ))}
          </div>

          <div className="te-overline mb-4">/ Thank you</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-none mb-6">
            A 30-second review<br />
            <span style={{ color: "var(--te-primary)" }}>changes everything</span> for us.
          </h1>
          <p className="text-zinc-300 text-lg leading-relaxed max-w-xl mx-auto mb-10">
            We're a small Adelaide team — not a national chain. Every Google review you leave
            directly helps another local family or business find us when their tech breaks. Thank you.
          </p>

          <a
            href={GOOGLE_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="review-write-btn"
            className="te-btn te-btn-primary inline-flex text-base px-8 py-4"
          >
            <Star size={18} fill="currentColor" stroke="none" />
            Write a Google Review
            <ArrowRight size={16} />
          </a>

          <p className="text-xs text-zinc-500 mt-4">
            Opens Google in a new tab · You'll need to be signed into your Google account
          </p>
        </div>
      </section>

      {/* What helps us most */}
      <section className="pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="te-card p-6 md:p-8" data-testid="review-tips">
            <div className="te-overline mb-4">/ What helps us most (if you've got the time)</div>
            <ul className="space-y-4 text-zinc-300 leading-relaxed">
              <li className="flex gap-3">
                <span className="font-bold mt-0.5" style={{ color: "var(--te-primary)" }}>01</span>
                <span>
                  <strong className="text-white">Mention your suburb.</strong>{" "}
                  "Came out to my place in Glenelg…" — these few words help us rank for that area.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="font-bold mt-0.5" style={{ color: "var(--te-primary)" }}>02</span>
                <span>
                  <strong className="text-white">Mention what we fixed.</strong>{" "}
                  "Sorted out my NBN modem", "recovered our family photos", "fixed the office
                  Wi-Fi". Specifics beat generic praise.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="font-bold mt-0.5" style={{ color: "var(--te-primary)" }}>03</span>
                <span>
                  <strong className="text-white">Name your tech if you remember.</strong>{" "}
                  A first name is plenty — it tells future customers a real human came out.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Had an issue? — soft funnel before bad reviews */}
      <section className="pb-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="border border-white/10 p-6 md:p-8"
            style={{ background: "var(--te-paper)" }}
            data-testid="review-issue-funnel"
          >
            <div className="flex items-start gap-4">
              <ShieldCheck size={24} className="flex-shrink-0 mt-1" style={{ color: "var(--te-primary)" }} />
              <div>
                <h3 className="text-xl font-bold text-white mb-2">
                  Hold on — something not right with our work?
                </h3>
                <p className="text-zinc-300 text-sm leading-relaxed mb-4">
                  Before you leave us a public review, please give us 5 minutes to fix it.
                  We're a local, owner-operated business and every job carries our{" "}
                  <strong className="text-white">no-fix-no-fee guarantee</strong> and a{" "}
                  <strong className="text-white">7-day warranty on our labour</strong>. Most
                  issues we hear about, we sort the same day at no extra cost.
                </p>
                <a
                  href={PHONE_TEL}
                  data-testid="review-issue-call-btn"
                  className="te-btn te-btn-ghost"
                >
                  <Phone size={16} /> Talk to us first · {PHONE}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes te-pop {
          0%   { transform: scale(0.3) rotate(-10deg); opacity: 0; }
          70%  { transform: scale(1.15) rotate(2deg); opacity: 1; }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
      `}</style>
    </>
  );
}
